import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchSalesOverview,
  type SalesOverviewOrder,
  type SalesOverviewPeriod,
  type SalesOverviewResponse,
} from '../api/reports';
import { borderRadius, colorExtensions, colors, spacing, typography, typographyFlat } from '../styles/tokens';
import { formatInstant } from '../utils/datetime';

function money(value?: number | null): string {
  if (value === undefined || value === null || Number.isNaN(Number(value))) {
    return '—';
  }
  return `$${Number(value).toFixed(2)}`;
}

function orderSortKey(order: SalesOverviewOrder): string {
  return order.ordered_at || order.date;
}

/** Newest sale first. Same-instant ties break on external id, newest label first. */
export function newestSalesOrders(orders: SalesOverviewOrder[]): SalesOverviewOrder[] {
  return [...orders].sort((a, b) => {
    const aKey = orderSortKey(a);
    const bKey = orderSortKey(b);
    if (aKey === bKey) {
      return (b.external_id || '').localeCompare(a.external_id || '');
    }
    return aKey < bKey ? 1 : -1;
  });
}

const PERIOD_METRICS: { key: keyof Pick<SalesOverviewPeriod, 'revenue' | 'orders' | 'fees' | 'net'>; label: string; money: boolean }[] = [
  { key: 'revenue', label: 'Revenue', money: true },
  { key: 'orders', label: 'Orders', money: false },
  { key: 'fees', label: 'Fees', money: true },
  { key: 'net', label: 'Net', money: true },
];

export const ReportsSalesOverviewPage: React.FC = () => {
  const [report, setReport] = useState<SalesOverviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  const recentOrders = useMemo(
    () => newestSalesOrders(report?.recent_orders ?? []),
    [report?.recent_orders],
  );

  const loadOverview = async () => {
    const current = ++requestId.current;
    try {
      setLoading(true);
      setError(null);
      const next = await fetchSalesOverview();
      if (current !== requestId.current) return;
      setReport(next);
    } catch (err: unknown) {
      if (current !== requestId.current) return;
      const status = (err as { response?: { status?: number } } | null)?.response?.status;
      if (status === 403) {
        setError('Admin role required to view Sales overview.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load sales overview');
      }
      setReport(null);
    } finally {
      if (current === requestId.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    void loadOverview();
  }, []);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Reports: Sales overview</h1>
        <p style={styles.subtitle}>
          Today, the last 7 days, and the last 30 days in America/Los_Angeles.
          Revenue, orders, fees, and net match Sales / COGS for the same dates and channel.
          Admin only.
        </p>
        <Link to="/reports/sales-cogs" style={styles.reportLink}>Open Sales / COGS</Link>
      </div>

      <div style={styles.toolbar}>
        <button type="button" style={styles.primaryButton} onClick={() => void loadOverview()} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {report && (
        <>
          <div style={styles.periodGrid}>
            {report.periods.map((period) => (
              <section key={period.key} style={styles.periodCard} aria-labelledby={`period-${period.key}`}>
                <h2 id={`period-${period.key}`} style={styles.periodTitle}>{period.label}</h2>
                <p style={styles.periodRange}>
                  {period.start_date} to {period.end_date}
                </p>
                <dl style={styles.metricList}>
                  {PERIOD_METRICS.map((metric) => (
                    <div key={metric.key} style={styles.metricItem}>
                      <dt style={styles.metricLabel}>{metric.label}</dt>
                      <dd style={styles.metricValue}>
                        {metric.money ? money(period[metric.key]) : String(period[metric.key])}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            ))}
          </div>

          <section aria-labelledby="recent-orders-heading" style={styles.section}>
            <h2 id="recent-orders-heading" style={styles.sectionTitle}>Recent orders</h2>
            <p style={styles.sectionNote}>
              Newest 20 sales in the last 30 days.
            </p>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Ordered</th>
                    <th style={styles.th}>Channel</th>
                    <th style={styles.th}>Order</th>
                    <th style={styles.th}>Revenue</th>
                    <th style={styles.th}>Fees</th>
                    <th style={styles.th}>Net</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.length === 0 && (
                    <tr>
                      <td style={styles.td} colSpan={6}>No orders in the last 30 days.</td>
                    </tr>
                  )}
                  {recentOrders.map((order) => (
                    <tr key={order.order_id}>
                      <td style={styles.td}>
                        {order.ordered_at ? formatInstant(order.ordered_at) : order.date}
                      </td>
                      <td style={styles.td}>{order.channel || '—'}</td>
                      <td style={styles.td}>{order.external_id || order.order_id}</td>
                      <td style={styles.td}>{money(order.revenue)}</td>
                      <td style={styles.td}>{money(order.fees)}</td>
                      <td style={styles.td}>
                        {money(order.net)}
                        {!order.cogs_complete && (
                          <span style={styles.incompleteBadge}> incomplete COGS</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section aria-labelledby="by-channel-heading" style={styles.section}>
            <h2 id="by-channel-heading" style={styles.sectionTitle}>By channel</h2>
            <p style={styles.sectionNote}>
              Each channel uses the Sales / COGS totals for that Los Angeles range.
              Net leaves out rows with incomplete COGS.
            </p>
            {report.periods.map((period) => (
              <section key={period.key} aria-labelledby={`channel-${period.key}`} style={styles.channelBlock}>
                <h3 id={`channel-${period.key}`} style={styles.channelTitle}>
                  {period.label}
                  <span style={styles.channelRange}>
                    {period.start_date} to {period.end_date}
                  </span>
                </h3>
                <div style={styles.tableWrap}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Channel</th>
                        <th style={styles.th}>Revenue</th>
                        <th style={styles.th}>Orders</th>
                        <th style={styles.th}>Fees</th>
                        <th style={styles.th}>Net</th>
                      </tr>
                    </thead>
                    <tbody>
                      {period.channels.length === 0 && (
                        <tr>
                          <td style={styles.td} colSpan={5}>No channel activity in this range.</td>
                        </tr>
                      )}
                      {period.channels.map((channel) => (
                        <tr key={`${period.key}-${channel.channel}`}>
                          <td style={styles.td}>{channel.channel || '—'}</td>
                          <td style={styles.td}>{money(channel.revenue)}</td>
                          <td style={styles.td}>{channel.orders}</td>
                          <td style={styles.td}>{money(channel.fees)}</td>
                          <td style={styles.td}>{money(channel.net)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
          </section>
        </>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: spacing.xl,
    maxWidth: 1400,
  },
  header: {
    marginBottom: spacing.lg,
  },
  title: {
    margin: 0,
    fontSize: typography.h1.fontSize,
    fontWeight: typography.h1.fontWeight,
    color: colors.text,
  },
  subtitle: {
    marginTop: spacing.sm,
    marginBottom: spacing.sm,
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
    maxWidth: 760,
  },
  reportLink: {
    color: colors.primary,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.medium,
  },
  toolbar: {
    marginBottom: spacing.lg,
  },
  primaryButton: {
    padding: `${spacing.sm} ${spacing.md}`,
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: borderRadius.md,
    cursor: 'pointer',
    fontWeight: typographyFlat.fontWeight.medium,
  },
  error: {
    padding: spacing.md,
    backgroundColor: colors.errorLight,
    color: colorExtensions.errorDark,
    borderRadius: borderRadius.md,
    marginBottom: spacing.md,
  },
  periodGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: spacing.md,
    marginBottom: spacing.xl,
  },
  periodCard: {
    backgroundColor: colors.backgroundAlt,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.lg,
    padding: spacing.md,
  },
  periodTitle: {
    margin: 0,
    fontSize: typography.h4.fontSize,
    fontWeight: typography.h4.fontWeight,
    color: colors.text,
  },
  periodRange: {
    marginTop: spacing.xs,
    marginBottom: spacing.md,
    color: colors.textSecondary,
    fontSize: typography.caption.fontSize,
  },
  metricList: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: spacing.sm,
    margin: 0,
  },
  metricItem: {
    margin: 0,
  },
  metricLabel: {
    fontSize: typography.caption.fontSize,
    color: colors.textSecondary,
  },
  metricValue: {
    margin: 0,
    fontSize: typography.h3.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
  },
  section: {
    marginBottom: spacing.xl,
  },
  sectionTitle: {
    margin: 0,
    fontSize: typography.h3.fontSize,
    fontWeight: typography.h3.fontWeight,
    color: colors.text,
  },
  sectionNote: {
    marginTop: spacing.xs,
    marginBottom: spacing.md,
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
    maxWidth: 760,
  },
  channelBlock: {
    marginBottom: spacing.lg,
  },
  channelTitle: {
    margin: 0,
    marginBottom: spacing.sm,
    fontSize: typography.h4.fontSize,
    fontWeight: typography.h4.fontWeight,
    color: colors.text,
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.sm,
    alignItems: 'baseline',
  },
  channelRange: {
    fontSize: typography.caption.fontSize,
    fontWeight: typography.caption.fontWeight,
    color: colors.textSecondary,
  },
  tableWrap: {
    overflowX: 'auto',
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.lg,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: typography.small.fontSize,
  },
  th: {
    textAlign: 'left',
    padding: spacing.sm,
    borderBottom: `1px solid ${colors.border}`,
    backgroundColor: colors.backgroundAlt,
  },
  td: {
    padding: spacing.sm,
    borderBottom: `1px solid ${colors.border}`,
    verticalAlign: 'top',
  },
  incompleteBadge: {
    color: colorExtensions.warningDark,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
};
